'use client';
import Box from '@mui/material/Box';
import { Children } from 'react';

// Hex literals because MUI grey tokens fail inside responsive border shorthands (e.g. { md: '1px solid' })
export const BORDER_DARK = '#1f2422'; // grey.800
export const BORDER_LIGHT = '#e2e5e3'; // grey.200

/**
 * Grid with automatic border dividers between items.
 *
 * Props:
 *   dark     — use dark border color (grey.800). Default: false (grey.200)
 *   cols     — number of columns at md+. Default: 4. xs always uses 2 columns.
 *   itemSx   — extra sx applied to each item wrapper (e.g. padding)
 *   gutter   — side padding next to the dividers, { xs, md }. The outer edges (first column left,
 *              last column right) get none, like PageHero's strip. Default: none (use itemSx)
 *   sx       — sx for the grid container
 *   children — each direct child becomes one grid cell
 */
export default function DividedGrid({
  dark = false,
  cols = 4,
  children,
  itemSx = {},
  gutter,
  sx = {},
  ...props
}) {
  const color = dark ? BORDER_DARK : BORDER_LIGHT;
  const items = Children.toArray(children);
  const n = items.length;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${cols}, 1fr)` },
        ...sx,
      }}
      {...props}
    >
      {items.map((child, i) => (
        <Box
          key={i}
          sx={{
            borderRight: {
              xs: i % 2 === 0 && i + 1 < n ? `1px solid ${color}` : 'none',
              md: i < n - 1 ? `1px solid ${color}` : 'none',
            },
            borderBottom: {
              xs: i < Math.ceil(n / 2) ? `1px solid ${color}` : 'none',
              md: 'none',
            },
            ...itemSx,
            ...(gutter && {
              pl: { xs: i % 2 === 1 ? gutter.xs : 0, md: i % cols > 0 ? gutter.md : 0 },
              pr: { xs: i % 2 === 0 && i + 1 < n ? gutter.xs : 0, md: i % cols < cols - 1 && i < n - 1 ? gutter.md : 0 },
            }),
          }}
        >
          {child}
        </Box>
      ))}
    </Box>
  );
}
